import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui'
import { waProxy } from '@/lib/whatsappProxy'

interface WebhookFailure {
  timestamp: string
  endpoint: string
  error: string
  httpStatus?: number
}

interface WebhookFailuresRowProps {
  orgId: string
  isExpanded: boolean
}

export default function WebhookFailuresRow({ orgId, isExpanded }: WebhookFailuresRowProps) {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['wa-webhook-failures', orgId],
    queryFn: () =>
      waProxy<{ failures: WebhookFailure[]; count: number }>(
        `/api/sessions/${encodeURIComponent(orgId)}/webhook-failures`
      ),
    enabled: isExpanded,
    staleTime: 5_000,
  })

  const clearMutation = useMutation({
    mutationFn: async () =>
      waProxy(`/api/sessions/${encodeURIComponent(orgId)}/webhook-failures`, 'DELETE'),
    onSuccess: async () => {
      await queryClient.setQueryData(['wa-webhook-failures', orgId], { failures: [], count: 0 })
      await queryClient.invalidateQueries({ queryKey: ['wa-webhook-failure-counts'] })
      toast.success('Webhook failures cleared.')
    },
    onError: (e) => {
      const message = e instanceof Error ? e.message : 'Failed to clear webhook failures.'
      toast.error(message)
    },
  })

  if (!isExpanded) return null
  const failures = data?.failures ?? []

  return (
    <tr className="border-t border-navy-border bg-navy-deeper/40">
      <td colSpan={6} className="px-4 py-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-primary">
              Webhook failures ({failures.length})
            </p>
            <Button
              size="sm"
              variant="ghost"
              className="text-danger hover:text-red-400"
              loading={clearMutation.isPending}
              onClick={async () => {
                if (failures.length === 0) return
                const confirmed = window.confirm(`Clear all webhook failures for ${orgId}?`)
                if (!confirmed) return
                await clearMutation.mutateAsync()
              }}
            >
              Clear All
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-ink-muted">Loading failures...</p>
          ) : failures.length === 0 ? (
            <p className="text-sm text-ink-muted">No webhook failures.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-navy-border">
              <table className="min-w-full text-xs bg-navy-card">
                <thead className="bg-navy-hover text-ink-secondary">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Timestamp</th>
                    <th className="text-left font-medium px-3 py-2">Endpoint</th>
                    <th className="text-left font-medium px-3 py-2">HTTP Status</th>
                    <th className="text-left font-medium px-3 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {failures.map((failure) => (
                    <tr key={`${failure.timestamp}-${failure.endpoint}`} className="border-t border-navy-border">
                      <td className="px-3 py-2 text-ink-secondary font-mono">{failure.timestamp}</td>
                      <td className="px-3 py-2 text-indigo-300 font-mono">{failure.endpoint}</td>
                      <td className="px-3 py-2 text-ink-secondary">{failure.httpStatus ?? '—'}</td>
                      <td className="px-3 py-2 text-danger">{failure.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}
